import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import {
  ArrowLeft,
  KeyRound,
  LayoutGrid,
  Pencil,
  Receipt,
  ShoppingBag,
  Trash2,
} from "lucide-react";
import { Button, Modal, Pill } from "@veorest/ui";
import { ROUTES, clientIdFromPath } from "../routes";
import { mapClientsRpcError } from "../lib/partnerClients";
import { mapAccountError, type AccountCredentials } from "../lib/partnerAccounts";
import {
  clientStatusLabelKey,
  clientStatusPillVariant,
} from "../lib/partnerReports";
import {
  useDeletePartnerClient,
  useResetPartnerClientPassword,
  useUpdatePartnerClient,
} from "../hooks/usePartnerClients";
import {
  usePartnerClientReport,
  usePartnerClientsOverview,
} from "../hooks/usePartnerReports";
import { usePartnerDashboardDateRange } from "../hooks/usePartnerDashboardDateRange";
import { OverviewDateControl } from "../components/dashboard/OverviewDateControl";
import { SegmentStrip } from "../components/data/SegmentStrip";
import {
  ClientEditModal,
  type ClientEditValues,
} from "../components/clients/ClientEditModal";
import { CredentialsModal } from "../components/accounts/CredentialsModal";
import { ResetPasswordModal } from "../components/accounts/ResetPasswordModal";
import { ClientOverviewTab } from "../components/clients/view/ClientOverviewTab";
import { ClientOrdersTab } from "../components/clients/view/ClientOrdersTab";
import { ClientStatementTab } from "../components/clients/view/ClientStatementTab";
import { useI18n } from "../i18n";

type ClientTab = "overview" | "orders" | "statement";

export default function ClientViewPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // Wildcard route (/app/*) → useParams has no :id; derive it from the path.
  const tenantId = clientIdFromPath(pathname);
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState<ClientTab>("overview");
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [credentials, setCredentials] = useState<AccountCredentials | null>(null);

  const { range, setToday, setCustom } = usePartnerDashboardDateRange("last30");
  const overviewQuery = usePartnerClientsOverview();
  const reportQuery = usePartnerClientReport(tenantId, range);
  const updateMutation = useUpdatePartnerClient();
  const deleteMutation = useDeletePartnerClient();
  const resetMutation = useResetPartnerClientPassword();

  // Range handoff from Analytics: /app/clients/:id?from=ISO&to=ISO
  const rangeInitRef = useRef(false);
  useEffect(() => {
    if (rangeInitRef.current) return;
    rangeInitRef.current = true;
    const fromRaw = searchParams.get("from");
    const toRaw = searchParams.get("to");
    if (!fromRaw || !toRaw) return;
    const from = new Date(fromRaw);
    const to = new Date(toRaw);
    if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return;
    setCustom(from, to);
  }, [searchParams, setCustom]);

  const client = useMemo(
    () => (overviewQuery.data ?? []).find((c) => c.id === tenantId) ?? null,
    [overviewQuery.data, tenantId]
  );

  const handleEditSubmit = async (values: ClientEditValues) => {
    if (!client) return;
    try {
      await updateMutation.mutateAsync({
        tenantId: client.id,
        patch: {
          name: values.name,
          phone: values.phone,
          address: values.address,
          username: values.username,
          email: values.email || null,
        },
      });
      toast.success(t("clients.toast.updated"));
      setEditOpen(false);
    } catch (err) {
      toast.error(t(mapClientsRpcError(err)));
    }
  };

  const handleResetPassword = async (password: string) => {
    if (!client) return;
    try {
      const result = await resetMutation.mutateAsync({
        tenantId: client.id,
        password,
      });
      setResetOpen(false);
      setCredentials(result);
    } catch (err) {
      toast.error(t(mapAccountError(err)));
    }
  };

  const handleDeleteConfirm = async () => {
    if (!client) return;
    try {
      await deleteMutation.mutateAsync({ tenantId: client.id });
      toast.success(t("clients.toast.deleted"));
      setDeleteOpen(false);
      navigate(ROUTES.CLIENTS);
    } catch (err) {
      toast.error(t(mapClientsRpcError(err)));
    }
  };

  const tabs = [
    { key: "overview" as const, label: t("clientView.tabs.overview"), icon: LayoutGrid },
    { key: "orders" as const, label: t("clientView.tabs.orders"), icon: ShoppingBag },
    { key: "statement" as const, label: t("clientView.tabs.statement"), icon: Receipt },
  ];

  if (!client && !overviewQuery.isLoading) {
    return (
      <div className="menu-page">
        <section className="menu-panel">
          <div className="menu-panel__body flex flex-col items-center gap-2 py-10 text-center">
            <p className="text-sm font-semibold text-[var(--text-secondary)]">
              {t("clientView.notFound")}
            </p>
            <Button
              variant="secondary"
              onClick={() => navigate(ROUTES.CLIENTS)}
              className="min-h-10 gap-2 px-4"
            >
              <ArrowLeft className="h-4 w-4 rtl:rotate-180" />
              {t("clientView.back")}
            </Button>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="menu-page">
      <header className="menu-page__header overview-page__header">
        <div className="min-w-0">
          <button
            type="button"
            onClick={() => navigate(ROUTES.CLIENTS)}
            className="inline-flex items-center gap-1.5 text-xs font-bold text-[var(--accent)]"
          >
            <ArrowLeft className="h-3.5 w-3.5 rtl:rotate-180" />
            {t("clientView.back")}
          </button>
          {client ? (
            <>
              <div className="mt-1 flex flex-wrap items-center gap-2">
                <h1 className="menu-page__title overview-page__title">
                  {client.name}
                </h1>
                <Pill variant={clientStatusPillVariant(client.tenant_status)}>
                  {t(clientStatusLabelKey(client.tenant_status))}
                </Pill>
                {client.currency ? (
                  <span className="rounded-full border border-[var(--border-subtle)] px-2 py-0.5 text-[11px] font-bold text-[var(--text-secondary)]">
                    {client.currency}
                  </span>
                ) : null}
              </div>
              <p className="menu-page__subtitle overview-page__subtitle">
                {client.username ? `@${client.username}` : client.slug}
                {client.phone ? ` · ${client.phone}` : ""}
                {client.address ? ` · ${client.address}` : ""}
              </p>
            </>
          ) : (
            <div className="mt-2 h-8 w-56 rounded-lg skeleton" />
          )}
        </div>

        <div className="menu-page__header-actions overview-page__header-actions">
          <OverviewDateControl
            range={range}
            onToday={setToday}
            onCustom={setCustom}
            compact
            showLiveBadge={false}
          />
          {client ? (
            <>
              <Button
                variant="secondary"
                onClick={() => setEditOpen(true)}
                className="min-h-10 gap-2 px-4"
              >
                <Pencil className="h-4 w-4" />
                {t("clients.edit")}
              </Button>
              {client.tenant_status.toLowerCase() !== "onboarding" ? (
                <Button
                  variant="secondary"
                  onClick={() => setResetOpen(true)}
                  className="min-h-10 gap-2 px-4"
                >
                  <KeyRound className="h-4 w-4" />
                  {t("accounts.resetAction")}
                </Button>
              ) : null}
              <Button
                variant="secondary"
                onClick={() => setDeleteOpen(true)}
                className="min-h-10 gap-2 px-4 text-[var(--danger)]"
              >
                <Trash2 className="h-4 w-4" />
                {t("clients.delete")}
              </Button>
            </>
          ) : null}
        </div>
      </header>

      <SegmentStrip
        value={tab}
        onChange={setTab}
        options={tabs}
        ariaLabel={t("clientView.tabsAria")}
      />

      {client ? (
        <>
          {tab === "overview" ? (
            <ClientOverviewTab
              client={client}
              report={reportQuery.data}
              loading={reportQuery.isLoading}
              range={range}
            />
          ) : null}
          {tab === "orders" ? (
            <ClientOrdersTab
              tenantId={client.id}
              currency={client.currency}
              range={range}
            />
          ) : null}
          {tab === "statement" ? (
            <ClientStatementTab
              tenantId={client.id}
              currency={client.currency}
              range={range}
            />
          ) : null}
        </>
      ) : null}

      {client && editOpen ? (
        <ClientEditModal
          key={client.id}
          open={editOpen}
          busy={updateMutation.isPending}
          client={client}
          usernameLocked={client.tenant_status.toLowerCase() !== "onboarding"}
          onClose={() => setEditOpen(false)}
          onSubmit={handleEditSubmit}
        />
      ) : null}

      {client ? (
        <ResetPasswordModal
          open={resetOpen}
          busy={resetMutation.isPending}
          targetLabel={client.name}
          username={client.username}
          onClose={() => setResetOpen(false)}
          onSubmit={handleResetPassword}
        />
      ) : null}

      <CredentialsModal
        open={!!credentials}
        title={client?.name ?? ""}
        subtitle={t("accounts.resetHint")}
        credentials={credentials}
        onClose={() => setCredentials(null)}
      />

      {client && deleteOpen ? (
        <Modal
          open
          onClose={() => {
            if (!deleteMutation.isPending) setDeleteOpen(false);
          }}
          panelClassName="max-w-sm"
        >
          <div className="px-5 py-4">
            <h2 className="text-lg font-extrabold text-[var(--text-primary)]">
              {t("clients.deleteTitle")}
            </h2>
            <p className="mt-2 text-sm text-[var(--text-secondary)]">
              {t("clients.deleteBody")}
            </p>
            <p className="mt-2 text-sm">
              <strong>{client.name}</strong>
              {client.username ? ` · @${client.username}` : ""}
            </p>
            <div className="mt-4 flex gap-2">
              <Button
                variant="secondary"
                className="min-h-11 flex-1"
                onClick={() => setDeleteOpen(false)}
                disabled={deleteMutation.isPending}
              >
                {t("common.cancel")}
              </Button>
              <Button
                variant="secondary"
                className="min-h-11 flex-1 border-[var(--danger)] text-[var(--danger)]"
                onClick={() => void handleDeleteConfirm()}
                disabled={deleteMutation.isPending}
              >
                {deleteMutation.isPending
                  ? t("clients.creating")
                  : t("clients.deleteConfirmCta", { name: client.name })}
              </Button>
            </div>
          </div>
        </Modal>
      ) : null}
    </div>
  );
}
